import React, { useEffect, useRef, useState } from "react"
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import Swal from "sweetalert2"
import moment from "moment"
import { FaFileUpload } from "react-icons/fa"
import {
  MdCurrencyPound,
  MdDownload,
  MdImportExport,
  MdReport,
} from "react-icons/md"
import Loader from "components/Loader/loader"
import {
  downloadReferenceSheet,
  getReferenceSheets,
  mapReferenceSheets,
  uploadReferenceSheet,
} from "./masterSheetApi"

const SECTIONS = [
  {
    type: "kpi",
    title: "KPI Sheet",
    description:
      "Reference copy of the signed-off KPI master sheet (GMP, OEE, Sales Revenue).",
    color: "#3b82f6",
    bg: "#eff6ff",
    border: "#bfdbfe",
    Icon: MdReport,
  },
  {
    type: "costings",
    title: "P&L Sheet",
    description:
      "Reference copy of the signed-off Profit & Loss master costing sheet.",
    color: "#16a34a",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    Icon: MdCurrencyPound,
  },
]

const titleOf = type => SECTIONS.find(s => s.type === type)?.title || "Sheet"

const MasterSheetReference = () => {
  document.title = "Master Sheet Reference | Robinsons"

  const [loading, setLoading] = useState(false)
  const [uploadDropdown, setUploadDropdown] = useState(false)
  const [uploadModal, setUploadModal] = useState(false)
  const [selectedType, setSelectedType] = useState("")
  const [uploadFile, setUploadFile] = useState(null)
  const [error, setError] = useState("")
  const [fetchError, setFetchError] = useState("")
  const [uploading, setUploading] = useState(false)
  // Latest uploaded file per sheet type + backend sheetId for the PUT URL
  const [uploaded, setUploaded] = useState({})
  const [sheetIds, setSheetIds] = useState({})
  const fileInputRef = useRef(null)

  const applyReferenceData = payload => {
    const mapped = mapReferenceSheets(payload)
    const nextUploaded = {}
    const nextIds = {}
    ;["kpi", "costings"].forEach(type => {
      const meta = mapped[type]
      if (meta?.sheetId) nextIds[type] = meta.sheetId
      // Only show the file block when the API actually returns a file.
      if (meta?.fileName) {
        nextUploaded[type] = {
          fileName: meta.fileName,
          fileUrl: meta.fileUrl || "",
          uploadedAt: meta.updatedAt || new Date(),
          status: meta.status || "",
        }
      }
    })
    setSheetIds(nextIds)
    setUploaded(nextUploaded)
  }

  const loadReferenceSheets = async () => {
    setLoading(true)
    const res = await getReferenceSheets()
    setLoading(false)
    if (res?.success) {
      setFetchError("")
      applyReferenceData(res)
    } else {
      setFetchError(res?.message || "Failed to load reference sheets.")
    }
  }

  useEffect(() => {
    loadReferenceSheets()
  }, [])

  const openUpload = type => {
    setSelectedType(type)
    setUploadFile(null)
    setError("")
    setUploadDropdown(false)
    setUploadModal(true)
  }

  const closeUpload = () => {
    if (uploading) return
    setUploadModal(false)
    setUploadFile(null)
    setError("")
  }

  const handleUpload = async () => {
    if (uploading) return
    if (!uploadFile) {
      setError("Please select a file.")
      return
    }
    const sheetId = sheetIds[selectedType]
    if (!sheetId) {
      setError(
        "Sheet ID not found for the selected sheet. Please reload the page."
      )
      return
    }
    setUploading(true)
    setLoading(true)
    const res = await uploadReferenceSheet({
      sheetId,
      file: uploadFile,
    })
    setLoading(false)
    setUploading(false)
    if (res?.success) {
      closeUpload()
      Swal.fire({
        title: "Reference Copy Uploaded",
        text: res?.message || `${titleOf(selectedType)} uploaded successfully.`,
        icon: "success",
      })
      // Refresh GET data so the cards show the latest uploaded file info.
      loadReferenceSheets()
    } else {
      Swal.fire({
        title: "Upload Failed",
        text: res?.message || "Something went wrong.",
        icon: "error",
      })
    }
  }

  const handleDownload = async type => {
    const meta = uploaded[type]
    if (!meta?.fileUrl) {
      Swal.fire({
        title: "File Not Found",
        text: "No file available to download.",
        icon: "error",
      })
      return
    }
    const res = await downloadReferenceSheet({
      fileUrl: meta.fileUrl,
      fileName: meta.fileName,
    })
    if (!res?.success) {
      Swal.fire({
        title: "Download Failed",
        text: res?.message || "Something went wrong.",
        icon: "error",
      })
    }
  }

  return (
    <div className="page-content">
      {loading && <Loader />}

      {/* ── Header ── */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          border: "1px solid #e2e8f0",
          padding: "18px 24px",
          marginBottom: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div>
            <h4
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "-0.3px",
              }}
            >
              Master Sheet Reference
            </h4>
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              Make a reference copy of the master costing sheet
            </span>
          </div>

          <Dropdown
            isOpen={uploadDropdown}
            toggle={() => setUploadDropdown(!uploadDropdown)}
          >
            <DropdownToggle
              title="Upload a reference sheet"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "0 14px",
                height: "36px",
                color: "#374151",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: "0.3px",
              }}
            >
              <MdImportExport size={15} style={{ color: "#6366f1" }} />
              Upload
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                style={{ marginLeft: "2px" }}
              >
                <path
                  d="M2 3.5L5 6.5L8 3.5"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </DropdownToggle>
            <DropdownMenu
              end
              style={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                padding: "6px",
                minWidth: "180px",
              }}
            >
              {SECTIONS.map(s => (
                <DropdownItem
                  key={s.type}
                  onClick={() => openUpload(s.type)}
                  style={{
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 500,
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#1e293b",
                  }}
                >
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "8px",
                      background: s.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <s.Icon size={14} color={s.color} />
                  </div>
                  {s.title}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      {fetchError && (
        <div
          className="d-flex align-items-center justify-content-between gap-3 flex-wrap"
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "12px",
            padding: "10px 16px",
            marginBottom: "16px",
            fontSize: "13px",
            color: "#991b1b",
          }}
        >
          <span>{fetchError}</span>
          <Button
            size="sm"
            color="danger"
            outline
            onClick={loadReferenceSheets}
            disabled={loading}
          >
            Retry
          </Button>
        </div>
      )}

      {/* ── KPI Sheet | P&L Sheet ── */}
      <Row className="g-3">
        {SECTIONS.map(section => (
          <Col xl={6} key={section.type}>
            <div
              className="bg-white rounded-4 shadow-sm"
              style={{
                height: "100%",
                padding: "24px",
                border: "1px solid #eef0f6",
              }}
            >
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: section.bg,
                    color: section.color,
                  }}
                >
                  <section.Icon size={22} />
                </div>
                <div>
                  <h5 className="mb-0 fw-semibold">{section.title}</h5>
                  <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                    Master reference
                  </span>
                </div>
              </div>

              <p style={{ fontSize: "13px", color: "#64748b", minHeight: "40px" }}>
                {section.description}
              </p>

              {/* Shows only after a file has been uploaded for this section */}
              {uploaded[section.type] ? (
                <div
                  className="d-flex align-items-center justify-content-between gap-3"
                  style={{
                    border: `1px solid ${section.border}`,
                    background: section.bg,
                    borderRadius: "12px",
                    padding: "12px 16px",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1e293b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {uploaded[section.type].fileName}
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      Uploaded{" "}
                      {moment(uploaded[section.type].uploadedAt).format(
                        "DD MMM YYYY, hh:mm A"
                      )}
                      {uploaded[section.type].status
                        ? ` • ${uploaded[section.type].status}`
                        : ""}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm d-inline-flex align-items-center justify-content-center border-0"
                    title={`Download ${section.title}`}
                    onClick={() => handleDownload(section.type)}
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "8px",
                      background: "#fff",
                      color: section.color,
                      flexShrink: 0,
                    }}
                  >
                    <MdDownload size={16} />
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    fontSize: "13px",
                    color: "#94a3b8",
                    textAlign: "center",
                    padding: "16px 0",
                  }}
                >
                  No file uploaded yet. Use the Upload button above.
                </div>
              )}
            </div>
          </Col>
        ))}
      </Row>

      {/* ── Upload modal ── */}
      <Modal isOpen={uploadModal} toggle={closeUpload} centered>
        <ModalHeader toggle={closeUpload} className="text-dark">
          Upload {titleOf(selectedType)} — Reference Copy
        </ModalHeader>
        <ModalBody>
          <Label>
            Select File <span className="text-danger">*</span>
          </Label>
          <Input
            type="file"
            innerRef={fileInputRef}
            accept=".xlsx,.xls,.csv"
            disabled={uploading}
            onChange={e => setUploadFile(e.target.files[0])}
          />
          {error && <span className="text-danger">* {error}</span>}
          <div className="d-flex justify-content-start gap-3 mt-3">
            <Button
              className="bg-blue"
              onClick={handleUpload}
              disabled={uploading || !sheetIds[selectedType]}
            >
              <FaFileUpload className="me-2" />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
          {!sheetIds[selectedType] && (
            <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "8px" }}>
              Waiting for sheet details to load. If this persists, reload the
              page.
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default MasterSheetReference
